// Hiệu lực giá cước và ngưỡng cảnh báo của bàn Pricing (LOGI-Mint-cho-chot.md · QĐ 24/Oct 10:17 + 11:04):
// biến động 10% · sắp hết 7 ngày · lệch bất thường > 20% so trung bình 4 tuần.
// Ngày ở đây là ngày lịch Asia/Ho_Chi_Minh (ValidFrom / ValidEnd), không phải ngày làm việc — giờ / ngày làm việc đi qua work-calendar.
// Chuỗi có múi giờ và Date luôn đổi sang giờ VN; không đọc múi giờ máy.

export const PRICING_THRESHOLDS = {
  /** Biến động giá so với giá trước đó của cùng tuyến. */
  changePct: 10,
  /** Còn ≤ N ngày tới ValidEnd thì là "sắp hết". */
  expiringDays: 7,
  /** Lệch so với trung bình 4 tuần. */
  anomalyPct: 20,
  anomalyWindowWeeks: 4,
} as const;

const VN_OFFSET_MS = 7 * 3_600_000;
const DAY_MS = 86_400_000;
const ZONED = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}(?::?\d{2})?)$/i;

/** Ngày lịch Asia/Ho_Chi_Minh (không DST, UTC+7) của một thời điểm, tính số ngày kể từ 1970-01-01. */
function vnDay(ms: number): number {
  return Math.floor((ms + VN_OFFSET_MS) / DAY_MS);
}

/** Hôm nay theo giờ VN, dạng `YYYY-MM-DD` — không phụ thuộc múi giờ máy / Worker (TZ=UTC). */
export function todayVN(now: Date = new Date()): string {
  return new Date(vnDay(now.getTime()) * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Ngày lịch VN, tính ra số ngày kể từ 1970-01-01. Nhận:
 * - Date / chuỗi có múi giờ (`…T17:00:00+00`, `…Z`) → đổi sang giờ VN (17:00Z = 00:00 VN hôm sau);
 * - `YYYY-MM-DD` hoặc chuỗi giờ không ghi múi giờ → lấy ngày ghi sẵn, coi là giờ VN.
 */
function dayNumber(value: string | Date): number {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('Ngày không hợp lệ');
    return vnDay(value.getTime());
  }
  if (ZONED.test(value)) {
    const iso = value
      .replace(' ', 'T')
      .replace(/([+-]\d{2})$/, '$1:00')
      .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) throw new Error(`Ngày không hợp lệ: ${value}`);
    return vnDay(ms);
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) throw new Error(`Ngày không hợp lệ: ${value}`);
  return Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}

/** Số ngày lịch từ `from` tới `to` (âm nếu `to` đã qua). */
export function daysBetween(from: string | Date, to: string | Date): number {
  return dayNumber(to) - dayNumber(from);
}

export type RateValidity = 'upcoming' | 'active' | 'expiring' | 'expired';

/**
 * Trạng thái hiệu lực của một dòng giá tại ngày `today`.
 * ValidEnd tính trọn ngày (hết hạn từ hôm sau). Thiếu ValidFrom thì coi như đã bắt đầu.
 */
export function rateValidity(
  rate: { validFrom?: string | null; validEnd: string },
  today: string | Date,
  expiringDays: number = PRICING_THRESHOLDS.expiringDays,
): { status: RateValidity; daysLeft: number } {
  const daysLeft = daysBetween(today, rate.validEnd);
  if (daysLeft < 0) return { status: 'expired', daysLeft };
  if (rate.validFrom && daysBetween(today, rate.validFrom) > 0) return { status: 'upcoming', daysLeft };
  return { status: daysLeft <= expiringDays ? 'expiring' : 'active', daysLeft };
}

/** % thay đổi từ `prev` sang `next` (làm tròn 1 chữ số); `prev` ≤ 0 thì `null`. */
export function changePct(prev: number, next: number): number | null {
  if (!(prev > 0) || !Number.isFinite(next)) return null;
  return Math.round(((next - prev) / prev) * 1000) / 10;
}

/** Biến động đủ lớn để cảnh báo (|Δ| ≥ ngưỡng). */
export function isSignificantChange(prev: number, next: number, pct: number = PRICING_THRESHOLDS.changePct): boolean {
  const d = changePct(prev, next);
  return d !== null && Math.abs(d) >= pct;
}

/** Lệch bất thường so với trung bình các giá trong cửa sổ (|Δ| > ngưỡng). Không có lịch sử thì không cảnh báo. */
export function isAnomalous(value: number, history: readonly number[], pct: number = PRICING_THRESHOLDS.anomalyPct): boolean {
  const xs = history.filter((x) => Number.isFinite(x) && x > 0);
  if (!xs.length) return false;
  const avg = xs.reduce((a, b) => a + b, 0) / xs.length;
  const d = changePct(avg, value);
  return d !== null && Math.abs(d) > pct;
}

/** Chỉ số dòng rẻ nhất (bỏ giá ≤ 0 / thiếu); hoà thì lấy dòng đầu. Không có dòng hợp lệ thì -1. */
export function cheapestIndex(prices: readonly (number | null | undefined)[]): number {
  let best = -1;
  prices.forEach((p, i) => {
    if (p != null && p > 0 && (best < 0 || p < prices[best]!)) best = i;
  });
  return best;
}
